import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, Loader2, ArrowLeft, Check, Upload, Image as ImageIcon,
  BadgeCheck, ShieldCheck, ExternalLink,
} from "lucide-react";

// Lightweight owner/admin "Edit Organization" surface (P1.1). Verified/Partner status are
// NOT editable here (admin-controlled) and the name/slug are intentionally read-only to keep
// public URLs stable. No create flow.
export default function EditOrganization() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(undefined); // undefined=loading, null=not found/denied
  const [tax, setTax] = useState(null);
  const [form, setForm] = useState(null);
  const [services, setServices] = useState(new Set());
  const [parentOptions, setParentOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([api.get(`/organizations/${id}`), api.get("/public/org-taxonomy")])
      .then(([o, t]) => {
        if (!alive) return;
        if (!o.data?.can_manage) { setOrg(null); return; }
        setOrg(o.data); setTax(t.data);
        setForm({
          type: o.data.type || "OTHER_REAL_ESTATE_SERVICE_PROVIDER",
          description: o.data.description || "",
          website: o.data.website || "",
          contact_email: o.data.contact_email || "",
          contact_mobile: o.data.contact_mobile || "",
          city: o.data.city || "",
          province: o.data.province || "",
          service_areas: (o.data.service_areas || []).join(", "),
          organization_unit_type: o.data.organization_unit_type || "NONE",
          parent_organization_id: o.data.parent_organization_id || "NONE",
          public_profile_enabled: !!o.data.public_profile_enabled,
        });
        setServices(new Set((o.data.services || []).map((s) => s.key)));
        // Candidate parents: published, publicly-visible orgs (excluding self).
        api.get("/public/organizations", { params: { page_size: 60 } })
          .then(({ data }) => {
            if (!alive) return;
            setParentOptions((data.items || []).filter((it) => it.id !== id));
          })
          .catch(() => {});
      })
      .catch(() => alive && setOrg(null));
    return () => { alive = false; };
  }, [id]);

  const suggested = useMemo(() => {
    if (!tax || !form) return [];
    return tax.type_service_suggestions?.[form.type] || [];
  }, [tax, form]);

  const orderedServices = useMemo(() => {
    if (!tax) return [];
    const sug = new Set(suggested);
    return [...tax.services].sort((a, b) => (sug.has(b.key) ? 1 : 0) - (sug.has(a.key) ? 1 : 0));
  }, [tax, suggested]);

  const toggleService = (key) => {
    setServices((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        description: form.description,
        website: form.website,
        contact_email: form.contact_email,
        contact_mobile: form.contact_mobile,
        city: form.city,
        province: form.province,
        service_areas: form.service_areas.split(",").map((s) => s.trim()).filter(Boolean),
        services: [...services],
        organization_unit_type: form.organization_unit_type === "NONE" ? null : form.organization_unit_type,
        parent_organization_id: form.parent_organization_id === "NONE" ? null : form.parent_organization_id,
        public_profile_enabled: form.public_profile_enabled,
      };
      const { data } = await api.put(`/organizations/${id}`, payload);
      setOrg(data);
      toast.success("Organization updated.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/organizations/${id}/${field}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrg(data);
      toast.success(`${field === "logo" ? "Logo" : "Cover"} updated.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading("");
    }
  };

  const publishToggle = async () => {
    try {
      const action = org.status === "PUBLISHED" ? "draft" : "publish";
      const { data } = await api.patch(`/organizations/${id}/status`, { action });
      setOrg(data);
      toast.success(action === "publish" ? "Organization published." : "Moved to draft.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (org === undefined) {
    return (<div className="min-h-screen bg-slate-50"><Navbar />
      <div className="flex items-center justify-center py-32 text-slate-400" data-testid="org-edit-loading"><Loader2 className="h-6 w-6 animate-spin" /></div></div>);
  }
  if (org === null) {
    return (<div className="min-h-screen bg-slate-50"><Navbar />
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="org-edit-denied">
        <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-[#0F2C59]">You can't manage this organization</h1>
        <p className="mt-2 text-sm text-slate-500">Only an organization owner or admin can edit its profile.</p>
        <Link to="/my-organizations" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2C59] hover:underline"><ArrowLeft className="h-4 w-4" /> My organizations</Link>
      </div></div>);
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="edit-organization">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="min-w-0">
            <Link to="/my-organizations" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F2C59] mb-1"><ArrowLeft className="h-4 w-4" /> My organizations</Link>
            <h1 data-testid="org-edit-name" className="font-display text-2xl font-bold text-[#0F2C59] truncate">{org.name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
              {org.is_verified && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 ring-1 ring-emerald-200"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span>}
              {org.is_partner && <span className="inline-flex items-center gap-1 rounded-full bg-[#C79A3B]/12 px-2 py-0.5 font-semibold text-[#8a6a1f] ring-1 ring-[#C79A3B]/30"><ShieldCheck className="h-3.5 w-3.5" /> Partner</span>}
              <span className="text-slate-400">Status: {org.status}</span>
              {org.slug && <a href={`/org/${org.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0F2C59] hover:underline">View public profile <ExternalLink className="h-3 w-3" /></a>}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-amber-50/60 px-4 py-2.5 text-[13px] text-amber-800 mb-5" data-testid="org-edit-admin-note">
          Verified & PropertyConnect Partner badges are granted by PropertyConnect and can't be changed here.
        </div>

        {/* Media */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
          <h2 className="text-sm font-semibold text-[#0F2C59] mb-3">Logo & cover</h2>
          <div className="flex flex-wrap items-center gap-4">
            <div className="h-20 w-20 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
              {org.logo ? <img src={org.logo} alt="" className="h-full w-full object-contain" /> : <Building2 className="h-8 w-8 text-slate-300" />}
            </div>
            <label className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3.5 py-2 text-sm font-semibold text-[#0F2C59] cursor-pointer hover:bg-slate-50" data-testid="org-logo-upload">
              {uploading === "logo" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Logo
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload("logo", e.target.files?.[0])} />
            </label>
            <label className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3.5 py-2 text-sm font-semibold text-[#0F2C59] cursor-pointer hover:bg-slate-50" data-testid="org-cover-upload">
              {uploading === "cover" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} Cover
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload("cover", e.target.files?.[0])} />
            </label>
          </div>
        </section>

        {/* Core */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-5 space-y-4">
          <div>
            <Label className="text-[#0F2C59]">Organization type</Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger data-testid="org-edit-type" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {tax.types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#0F2C59]">About</Label>
            <Textarea data-testid="org-edit-description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={4} className="mt-1.5 rounded-xl" placeholder="Describe your organization…" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-[#0F2C59]">City</Label><Input data-testid="org-edit-city" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
            <div><Label className="text-[#0F2C59]">Province</Label><Input data-testid="org-edit-province" value={form.province} onChange={(e) => setForm((f) => ({ ...f, province: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
          </div>
          <div>
            <Label className="text-[#0F2C59]">Service areas</Label>
            <Input data-testid="org-edit-service-areas" value={form.service_areas} onChange={(e) => setForm((f) => ({ ...f, service_areas: e.target.value }))} className="mt-1.5 rounded-xl" placeholder="Metro Manila, Cebu, Davao (comma-separated)" />
          </div>
        </section>

        {/* Structure & hierarchy (P1.2A) — optional; describes organizational structure */}
        <section className="rounded-2xl border border-[#0F2C59]/15 bg-[#0F2C59]/[0.03] p-5 mb-5 space-y-4" data-testid="org-edit-structure">
          <div>
            <h2 className="text-sm font-semibold text-[#0F2C59]">Structure &amp; affiliation</h2>
            <p className="text-[13px] text-slate-500">Optional. For associations with chapters or member boards, or organizations that belong to a larger body.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#0F2C59]">Organization unit</Label>
              <Select value={form.organization_unit_type} onValueChange={(v) => setForm((f) => ({ ...f, organization_unit_type: v }))}>
                <SelectTrigger data-testid="org-edit-unit-type" className="mt-1.5 rounded-xl"><SelectValue placeholder="Not specified" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Not specified</SelectItem>
                  {(tax.unit_types || []).map((u) => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#0F2C59]">Parent organization</Label>
              <Select value={form.parent_organization_id} onValueChange={(v) => setForm((f) => ({ ...f, parent_organization_id: v }))}>
                <SelectTrigger data-testid="org-edit-parent" className="mt-1.5 rounded-xl"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  {parentOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[12px] text-slate-400">e.g. a chapter linking to its national association.</p>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
          <h2 className="text-sm font-semibold text-[#0F2C59]">Key services</h2>
          <p className="text-[13px] text-slate-500 mb-3">Select the services your organization offers. Suggestions for your type appear first.</p>
          <div className="flex flex-wrap gap-2" data-testid="org-edit-services">
            {orderedServices.map((s) => {
              const on = services.has(s.key);
              const isSug = suggested.includes(s.key);
              return (
                <button key={s.key} type="button" data-testid={`org-service-${s.key}`} onClick={() => toggleService(s.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${on ? "bg-[#0F2C59] text-white" : isSug ? "bg-[#0F2C59]/8 text-[#0F2C59] ring-1 ring-[#0F2C59]/20" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {on && <Check className="h-3.5 w-3.5" />}{s.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Contact */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-5 space-y-4">
          <h2 className="text-sm font-semibold text-[#0F2C59]">Public contact</h2>
          <div><Label className="text-[#0F2C59]">Website</Label><Input data-testid="org-edit-website" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} className="mt-1.5 rounded-xl" placeholder="https://…" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-[#0F2C59]">Email</Label><Input data-testid="org-edit-email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
            <div><Label className="text-[#0F2C59]">Mobile</Label><Input data-testid="org-edit-mobile" value={form.contact_mobile} onChange={(e) => setForm((f) => ({ ...f, contact_mobile: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
          </div>
        </section>

        {/* Visibility */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-5 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-[#0F2C59]">Show on public directory</h2>
            <p className="text-[13px] text-slate-500">When on, your published profile appears in Connections discovery.</p>
          </div>
          <Switch data-testid="org-edit-public-toggle" checked={form.public_profile_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, public_profile_enabled: v }))} />
        </section>

        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="org-edit-save" onClick={save} disabled={saving} className="rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Check className="h-4 w-4 mr-1.5" />} Save changes
          </Button>
          <Button data-testid="org-edit-publish" onClick={publishToggle} variant="outline" className="rounded-xl">
            {org.status === "PUBLISHED" ? "Move to draft" : "Publish"}
          </Button>
        </div>
      </div>
    </div>
  );
}
